import { ServiceClient } from 'src/utils'
import type {
    BillingFilter,
    BillingInvoice,
    BillingFilterOptions,
    BillingLine,
    BillingOriginLine,
    BillingOriginQuery,
    BillingSummary,
    BillingUserLine,
    InvoiceLineInput,
    PriceTableVersion,
    ReverseInvoiceInput,
    SavePriceTableInput
} from './bl_types'

const serviceClient = ServiceClient.singleton()

export class BillingService {
    static #INSTANCE = new BillingService()
    static readonly singleton = () => BillingService.#INSTANCE

    async fetchPriceTable(groupId?: number | null): Promise<PriceTableVersion | null> {
        return serviceClient.authGet<PriceTableVersion | null>(url('/api/billing/price-table', { groupId }))
    }

    async fetchPriceTableHistory(groupId?: number | null): Promise<PriceTableVersion[]> {
        return serviceClient.authGet<PriceTableVersion[]>(url('/api/billing/price-table/history', { groupId }))
    }

    async savePriceTable(input: SavePriceTableInput): Promise<PriceTableVersion> {
        return serviceClient.authPost<PriceTableVersion>('/api/billing/price-table', input)
    }

    async resetGroupPriceTable(groupId: number): Promise<void> {
        await serviceClient.authDelete(`/api/billing/price-table/group/${groupId}`)
    }

    async fetchReport(filter: BillingFilter): Promise<BillingLine[]> {
        return serviceClient.authGet<BillingLine[]>(url('/api/billing/report', filter))
    }

    async fetchSummary(filter: BillingFilter): Promise<BillingSummary> {
        return serviceClient.authGet<BillingSummary>(url('/api/billing/report/summary', filter))
    }

    async fetchUserBreakdown(groupId: number, filter: BillingFilter): Promise<BillingUserLine[]> {
        return serviceClient.authGet<BillingUserLine[]>(
            url(`/api/billing/report/group/${groupId}/users`, { ...filter, groupId: undefined })
        )
    }

    async fetchOrigins(query: BillingOriginQuery): Promise<BillingOriginLine[]> {
        return serviceClient.authGet<BillingOriginLine[]>(url('/api/billing/report/origins', query))
    }

    async downloadOriginsXlsx(query: BillingOriginQuery): Promise<Blob> {
        return downloadXlsx('/api/billing/report/origins/xlsx', query)
    }

    async invoiceLine(input: InvoiceLineInput): Promise<BillingInvoice> {
        return serviceClient.authPost<BillingInvoice>('/api/billing/invoice', input)
    }

    async reverseInvoice(input: ReverseInvoiceInput): Promise<{ reversed: boolean }> {
        return serviceClient.authPost<{ reversed: boolean }>('/api/billing/invoice/reverse', input)
    }

    async fetchFilterOptions(): Promise<BillingFilterOptions> {
        return serviceClient.authGet<BillingFilterOptions>('/api/billing/filter-options')
    }

    async downloadXlsx(filter: BillingFilter): Promise<Blob> {
        return downloadXlsx('/api/billing/report/xlsx', filter)
    }
}

async function downloadXlsx(path: string, body: unknown): Promise<Blob> {
    const response = await serviceClient.authRawPost(path, body)

    if (response.status !== 200 && response.status !== 201) {
        let message = response.statusText
        try {
            const json = await response.json()
            message = json?.message || message
        } catch {
            // corpo não é JSON: mantém o statusText
        }
        throw new Error(message || 'Falha ao gerar a planilha')
    }

    return response.blob()
}

type UrlParams = {
    startMonth?: string
    endMonth?: string
    groupId?: number | null
    status?: string
    entityType?: string
    entityId?: number
    byUser?: boolean
    byVersion?: boolean
}

function url(path: string, params: UrlParams): string {
    const search = new URLSearchParams()

    if (params.startMonth) {
        search.set('startMonth', params.startMonth)
    }
    if (params.endMonth) {
        search.set('endMonth', params.endMonth)
    }
    if (params.groupId) {
        search.set('groupId', String(params.groupId))
    }
    if (params.status) {
        search.set('status', params.status)
    }
    if (params.entityType && params.entityId) {
        search.set('entityType', params.entityType)
        search.set('entityId', String(params.entityId))
    }
    if (params.byUser) {
        search.set('byUser', 'true')
    }
    if (params.byVersion) {
        search.set('byVersion', 'true')
    }

    const query = search.toString()
    return query ? `${path}?${query}` : path
}

export default BillingService.singleton()
